const StructuralMutationTypes = new Set([
  'block-added',
  'block-removed',
  'block-moved',
]);

/** True when EditorJS reports a structural (add/remove/move) mutation. */
export function isStructuralEditorChange(
  event: { type: string } | { type: string }[] | undefined,
): boolean {
  if (!event) return false;
  const events = Array.isArray(event) ? event : [event];
  return events.some(e => StructuralMutationTypes.has(e.type));
}

/**
 * Resolves an EditorJS block id to a Redux-backed document UUID.
 * Never returns a raw EditorJS short id — callers must treat `null` as
 * "not ready" and abort the structural action.
 */
export function resolveSyncedDocId(
  editorBlockId: string,
  idMapping: Map<string, string>,
  byId: Record<string, unknown>,
): string | null {
  const mapped = idMapping.get(editorBlockId);
  const candidate =
    mapped ?? (editorBlockId in byId ? editorBlockId : undefined);
  if (!candidate || !(candidate in byId)) return null;
  return candidate;
}

/**
 * True when the current DOM selection is collapsed with nothing (no text,
 * no chip) between it and the very start of `root`. Used to detect "caret at
 * position 0 of the block" for the backspace-merge guard below — `Range#
 * toString()` includes text nodes even inside `contenteditable="false"`
 * descendants (e.g. a leading ref-chip), so a non-empty result correctly
 * rules out "nothing precedes the caret" in that case too.
 */
export function isCaretAtStartOfElement(root: HTMLElement): boolean {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0 || !selection.isCollapsed) {
    return false;
  }
  const range = selection.getRangeAt(0);
  if (!root.contains(range.startContainer)) return false;

  const preRange = document.createRange();
  preRange.selectNodeContents(root);
  preRange.setEnd(range.startContainer, range.startOffset);
  return preRange.toString().length === 0;
}

/**
 * Resolves the previous EditorJS block's element within the same holder,
 * given the block element that currently has focus. Returns `null` when
 * `current` is the first block (nothing to merge into).
 */
export function findPreviousBlockElement(
  current: HTMLElement,
): HTMLElement | null {
  const previous = current.previousElementSibling;
  if (previous instanceof HTMLElement && previous.classList.contains('ce-block')) {
    return previous;
  }
  return null;
}
